import qs from 'qs';

import {getCSRFToken} from './auth.js';
import {getCurrentEnv, getEnvBaseUrl} from './environment.js';
import {isAbortError} from './general.js';

export enum RequestMethod {
  INFO = 'INFO',
  POST = 'POST',
  GET = 'GET',
  PUT = 'PUT',
  PATCH = 'PATCH',
  DELETE = 'DELETE',
}

export interface ApiError extends Error {
  status?: number;
}

export interface ApiValidationError extends ApiError {
  errors: string[];
}

function isObjectWithErrorsArray(error: unknown): error is {errors: string[]} {
  if (error === null || error === undefined) return false;
  if (typeof error !== 'object') return false;
  if (!('errors' in error)) return false;
  const value = (error as {errors: unknown}).errors;
  return (
    Array.isArray(value) &&
    value.every((e): e is string => typeof e === 'string')
  );
}

export function isApiValidationError(
  error: unknown
): error is ApiValidationError {
  if (error === null || error === undefined) return false;
  if (typeof error !== 'object') return false;
  if (!('message' in error) || !('errors' in error)) return false;
  const value = error as {message: unknown; errors: unknown};
  return (
    typeof value.message === 'string' &&
    Array.isArray(value.errors) &&
    value.errors.length > 0 &&
    value.errors.every((e): e is string => typeof e === 'string')
  );
}

/** V3 API errors use { errors: string[] }. Returns joined non-empty errors or fallback. */
export function getApiErrorMessage(error: unknown, fallback: string): string {
  if (isObjectWithErrorsArray(error) && error.errors.length > 0) {
    return error.errors.join('. ');
  }
  return fallback;
}

/**
 * Simple interface for fetcher parameters.
 */
export interface FetcherParams<K = unknown> {
  method: RequestMethod;
  body?: K | Record<string, unknown> | unknown;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  redirect?: RequestRedirect;
  responseParser?: 'json' | 'formdata' | 'text' | 'blob' | 'url' | null;
}

async function parseErrorResponse(res: Response): Promise<unknown> {
  const body = await res.text();
  if (!body) return {};

  try {
    return JSON.parse(body) as unknown;
  } catch {
    return body;
  }
}

function apiErrorFromResponse(
  data: unknown,
  res: Response
): ApiError | ApiValidationError {
  const payload = data && typeof data === 'object' ? data : {};
  const message =
    'message' in payload && typeof payload.message === 'string'
      ? payload.message
      : typeof data === 'string' && data.trim()
        ? data
        : `Request failed with status ${res.status}.`;
  const errors =
    'errors' in payload &&
    Array.isArray(payload.errors) &&
    payload.errors.length > 0 &&
    payload.errors.every((error): error is string => typeof error === 'string')
      ? payload.errors
      : undefined;
  const error = new Error(message) as ApiError;
  error.status = res.status;

  if (errors) {
    return Object.assign(error, {errors});
  }

  return error;
}

export function getQueryParameter<T extends string>(key: string): null | T {
  const queryParams = new URLSearchParams(location.search);
  if (!queryParams.has(key)) return null;
  return queryParams.get(key) as T;
}

/**
 * Simple fetch wrap to make native fetch more usable.
 */
export async function fetcher<T, K = unknown>(
  url: string,
  params: FetcherParams<K>
): Promise<T> {
  const headers: Record<string, string> =
    params.body instanceof FormData ? {} : {'Content-Type': 'application/json'};

  if (getCSRFToken()) {
    headers['X-CSRF-Token'] = getCSRFToken();
  }

  const requestData: RequestInit = {
    signal: params.signal,
    method: params.method,
    redirect: params.redirect,
    headers: {
      ...(headers || {}),
      ...params.headers,
    },
  };

  if (params.body) {
    requestData.body =
      params.body instanceof FormData
        ? params.body
        : JSON.stringify(params.body);
  }

  try {
    const res = await fetch(url, requestData);
    if (!res.ok) {
      const data = await parseErrorResponse(res);
      return Promise.reject(apiErrorFromResponse(data, res));
    }

    let data;
    switch (params.responseParser) {
      case null:
        data = '';
        break;
      case 'formdata':
        data = await res.formData();
        break;
      case 'blob':
        data = await res.blob();
        break;
      case 'text':
        data = await res.text();
        break;
      case 'url':
        data = res.redirected ? res.url : '';
        break;
      default:
        data = await res.json();
        break;
    }

    return Promise.resolve(data);
  } catch (err) {
    if (!isAbortError(err)) {
      console.error(err);
    }

    return Promise.reject(err);
  }
}

export const getSafeBaseUrl = (url?: string): string => {
  if (url) return url;

  if (typeof window !== 'undefined') {
    const envUrl = getEnvBaseUrl();
    if (!envUrl || getCurrentEnv() === 'production') {
      return window.location.href;
    }
    return envUrl;
  }

  return getEnvBaseUrl() || 'http://localhost';
};

export type URLBuilderParams = Record<
  string,
  (string | number)[] | string | number
>;

export function buildURL(pathStr: string, params: URLBuilderParams = {}): URL {
  const baseURL = `${location.protocol}//${location.host}`;
  const url = new URL(baseURL + pathStr);

  Object.keys(params).forEach(k => {
    const v = params[k];

    if (typeof v === 'string' || typeof v === 'number') {
      url.searchParams.set(k, String(v));
    }

    if (Array.isArray(v)) {
      v.forEach(value => {
        url.searchParams.append(`${k}[]`, String(value));
      });
    }
  });

  return url;
}

export function getDefaultHeaders(): Record<string, string> {
  return {
    'Content-Type': 'application/json',
    'X-CSRF-Token': getCSRFToken(),
  };
}

export const attachParamsToCurrentUrlAndNavigate = (
  params: URLBuilderParams
): void => {
  const url = buildURL(location.pathname, params);
  location.href = url.toString();
};

export type PresenterParams = {
  fields: Record<string, string[]>;
  includes?: string[];
};

export type ExcludePresenterKeys<T> =
  T extends Record<string, unknown> ? Omit<T, keyof PresenterParams> : T;

function validateNoPresenterKeyConflict<T>(additionalData: T): void {
  if (
    additionalData &&
    typeof additionalData === 'object' &&
    !Array.isArray(additionalData)
  ) {
    const presenterKeys: (keyof PresenterParams)[] = ['fields', 'includes'];
    const dataKeys = Object.keys(additionalData);
    const conflictingKeys = presenterKeys.filter(key => dataKeys.includes(key));

    if (conflictingKeys.length > 0) {
      throw new Error(
        `Additional data cannot contain reserved PresenterParams keys: ${conflictingKeys.join(', ')}`
      );
    }
  }
}

/**
 * Builds a URL with presenter params (fields and includes) for Rails API endpoints.
 * Uses the qs library to properly format Rails-style bracket notation.
 *
 * @param pathStr - The API path
 * @param presenterParams - Presenter configuration with fields and includes
 * @param additionalParams - Additional query parameters (cannot contain 'fields' or 'includes' keys)
 *
 * @example
 * buildPresenterURL('/api/v3/metrics', {
 *   fields: {
 *     metrics: ['id', 'name', 'status'],
 *     source_connection: ['id', 'source_type']
 *   },
 *   includes: ['source_connection', 'created_by']
 * })
 */
export function buildPresenterURL<
  T extends Record<string, string | number | boolean | unknown>,
>(
  pathStr: string,
  presenterParams: PresenterParams,
  additionalParams?: ExcludePresenterKeys<T>
): string {
  const baseURL = `${location.protocol}//${location.host}`;

  if (additionalParams) {
    validateNoPresenterKeyConflict(additionalParams);
  }

  const params = {
    ...presenterParams,
    ...additionalParams,
  };

  // Use qs to stringify with Rails-style bracket notation
  const queryString = qs.stringify(params, {arrayFormat: 'brackets'});

  return `${baseURL}${pathStr}?${queryString}`;
}

/**
 * Builds a request body with presenter params for POST/PUT/PATCH requests.
 * The nested JSON structure is sent as-is to Rails, which parses it natively.
 *
 * @param presenterParams - Presenter configuration with fields and includes
 * @param additionalData - Additional data to include in the body (cannot contain 'fields' or 'includes' keys)
 *
 * @example
 * const body = buildPresenterBody({
 *   fields: {
 *     metrics: ['id', 'name', 'description'],
 *     source_connection: ['id', 'source_type']
 *   },
 *   includes: ['source_connection', 'created_by']
 * }, { search: 'revenue', status: 'active' });
 *
 * fetcher('/api/v3/metrics/search', {
 *   method: RequestMethod.POST,
 *   body
 * });
 */
export function buildPresenterBody<T extends Record<string, unknown>>(
  presenterParams: PresenterParams,
  additionalData?: ExcludePresenterKeys<T>
): PresenterParams & ExcludePresenterKeys<T> {
  if (additionalData) {
    validateNoPresenterKeyConflict(additionalData);
  }

  return {
    ...presenterParams,
    ...additionalData,
  } as PresenterParams & ExcludePresenterKeys<T>;
}
